import React, { useEffect, useState } from 'react';
import { Icon, StatusChip } from '../components/sp-primitives';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';

export function PCompanies() {
  const app = useApp();
  const canCreateCompany = Array.isArray(app.currentUser?.permissions) && app.currentUser.permissions.includes('Administrador');
  const [siengeLiveById, setSiengeLiveById] = useState({});
  const [siengeLiveLoading, setSiengeLiveLoading] = useState(false);
  const [siengeLiveError, setSiengeLiveError] = useState(null);
  const [siengeLiveCheckedAt, setSiengeLiveCheckedAt] = useState(null);

  useEffect(() => {
    if (app.route !== 'companies' || !app.authToken) return undefined;
    let cancelled = false;
    (async () => {
      const activeCompanies = (app.companies || []).filter((c) => String(c.status || '').toLowerCase() === 'active');
      if (!activeCompanies.length) {
        setSiengeLiveById({});
        setSiengeLiveCheckedAt(null);
        setSiengeLiveError(null);
        setSiengeLiveLoading(false);
        return;
      }
      setSiengeLiveLoading(true);
      setSiengeLiveError(null);
      setSiengeLiveById({});
      try {
        const data = await app.fetchCompaniesSiengeLive();
        if (cancelled) return;
        const map = {};
        const activeIds = new Set(activeCompanies.map((c) => Number(c.id)));
        (data?.results || []).forEach((r) => {
          const rowCompanyId = Number(r.company_id || 0);
          if (activeIds.has(rowCompanyId)) {
            map[rowCompanyId] = r;
          }
        });
        setSiengeLiveById(map);
        setSiengeLiveCheckedAt(data?.checked_at || null);
      } catch (e) {
        if (!cancelled) {
          setSiengeLiveError(e.message || 'Falha ao verificar API');
          setSiengeLiveById({});
        }
      } finally {
        if (!cancelled) setSiengeLiveLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [app.route, app.authToken, app.companies]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderSiengeChip = (c) => {
    if (String(c.status || '').toLowerCase() !== 'active') {
      return <StatusChip kind="neutral" dot={false}>API Inativada</StatusChip>;
    }
    if (siengeLiveLoading && !siengeLiveById[c.id]) {
      return <StatusChip kind="neutral" dot={false}>Verificando API…</StatusChip>;
    }
    const row = siengeLiveById[c.id];
    if (siengeLiveError && !row) {
      return <StatusChip kind="amber" dot={false}>Status API indisponível</StatusChip>;
    }
    if (!row) {
      if (c.sienge_credentials_configured) {
        return <StatusChip kind="neutral" dot={false}>API não verificada</StatusChip>;
      }
      return <StatusChip kind="amber" dot={false}>API Sienge não configurada</StatusChip>;
    }
    if (!row.configured) {
      return <StatusChip kind="amber" dot={false}>API Sienge não configurada</StatusChip>;
    }
    if (row.ok === true) {
      return <StatusChip kind="green" dot={false}>API ligada</StatusChip>;
    }
    return (
      <span className="sp-company-api-chip-wrap" title={row.message || 'Falha na API Sienge'}>
        <StatusChip kind="red" dot={false}>API com falha</StatusChip>
      </span>
    );
  };

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="companies" title="A carregar empresas…" />;
  }

  return (
    <PShell active="companies">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Integrações</div>
          <h1 className="sp-screen-head__title">Empresas cadastradas</h1>
          {siengeLiveCheckedAt && (
            <div className="sp-screen-head__meta sp-company-live-meta">
              Última verificação da API Sienge: {new Date(siengeLiveCheckedAt).toLocaleString('pt-BR')}
            </div>
          )}
          {siengeLiveError && (
            <div className="sp-screen-head__meta sp-company-live-meta sp-company-live-meta--warn">
              {siengeLiveError} — exibindo status parcial.
            </div>
          )}
        </div>
        {canCreateCompany && (
          <button type="button" className="sp-btn sp-btn-accent" onClick={() => app.navigate('company-new')}><Icon name="plus" size={13} /> Criar nova empresa</button>
        )}
      </div>
      <div className="sp-company-list">
        {app.companies.map((c) => (
          <div key={c.id} className="sp-card sp-company-card">
            <div className="sp-company-avatar">{c.name[0]}</div>
            <div>
              <div className="sp-company-title">{c.name}</div>
              <div className="sp-mono sp-company-meta">company_id:{c.id} · doc:{c.document || '-'}</div>
              <div style={{ marginTop: 6 }}>
                {String(c.status || '').toLowerCase() === 'active' ? (
                  <StatusChip kind="green">ativa</StatusChip>
                ) : (
                  <StatusChip kind="red">inativa</StatusChip>
                )}
              </div>
            </div>
            <div className="sp-company-actions">
              {renderSiengeChip(c)}
              <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => app.navigate('company-sync-logs', { companyId: c.id })}><Icon name="history" size={12} /> Logs</button>
              <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => app.navigate('company-creditors', { companyId: c.id })}><Icon name="users" size={12} /> Credores</button>
              <button type="button" className="sp-btn sp-btn-outline sp-btn-tiny" onClick={() => app.navigate('company-new', { companyId: c.id })}><Icon name="edit" size={12} /> Detalhes</button>
            </div>
          </div>
        ))}
        {app.companies.length === 0 && (
          <div className="sp-card sp-empty-hint-card">
            Nenhuma empresa cadastrada.
          </div>
        )}
      </div>
    </PShell>
  );
}
